import {
  AppShellSidebar,
  AppShellSidebarBrand,
  AppShellSidebarBrandLabel,
  AppShellSidebarChild,
  AppShellSidebarChildren,
  AppShellSidebarFooter,
  AppShellSidebarGroup,
  AppShellSidebarGroupChevron,
  AppShellSidebarItem,
  AppShellSidebarNav,
  BrandMark,
  Button,
  ShellIconChevronLeft,
  ShellIconChevronRight,
} from '@zrsystem/ui';
import type { KeyboardEvent, ReactNode } from 'react';

import { isShellNavChildLinkActive, isShellNavPathActive } from '@/shared/shell/nav-active.js';
import type {
  ShellNavChildLink,
  ShellNavEntry,
  ShellNavGroup,
  ShellNavLink,
  ShellNavTaxonomyEntity,
} from '@/shared/shell/nav.types.js';
import { isShellNavChildRoute, isShellNavGroup } from '@/shared/shell/nav.types.js';

export interface ExpandableNavSidebarProps {
  entries: readonly ShellNavEntry[];
  pathname: string;
  expanded: boolean;
  openGroups: Readonly<Record<string, boolean>>;
  activeQuickPanelEntity?: ShellNavTaxonomyEntity | null;
  brandLabel?: string;
  footerExtras?: ReactNode;
  onExpandedChange: (expanded: boolean) => void;
  onNavigate: (path: string) => void;
  onOpenGroup: (groupId: string) => void;
  onCloseGroup: (groupId: string) => void;
  onOpenQuickPanel?: (panelKey: string, params: Record<string, unknown>) => void;
  onOpenFormDrawer?: (drawerKey: string, params: Record<string, unknown>) => void;
}

function resolveLinkEnabled(entry: ShellNavLink): boolean {
  return entry.enabled !== false && entry.path !== undefined;
}

function resolveHomeLink(
  entries: readonly ShellNavEntry[],
): { label: string; path: string } | undefined {
  for (const entry of entries) {
    if (entry.kind === 'link' && entry.id === 'home' && entry.path !== undefined) {
      return { label: entry.label, path: entry.path };
    }
  }

  return undefined;
}

function FlatNavItem({
  entry,
  pathname,
  expanded,
  onNavigate,
}: {
  entry: ShellNavLink;
  pathname: string;
  expanded: boolean;
  onNavigate: (path: string) => void;
}) {
  const Icon = entry.icon;
  const enabled = resolveLinkEnabled(entry);
  const active = enabled && isShellNavPathActive(pathname, entry);

  return (
    <AppShellSidebarItem
      active={active}
      aria-label={entry.label}
      aria-current={active ? 'page' : undefined}
      disabled={!enabled}
      title={enabled ? entry.label : `${entry.label} (em breve)`}
      icon={<Icon size={16} />}
      label={expanded ? entry.label : undefined}
      onClick={() => {
        if (!enabled || entry.path === undefined) {
          return;
        }

        onNavigate(entry.path);
      }}
    />
  );
}

function GroupNavItem({
  entry,
  pathname,
  expanded,
  openGroups,
  activeQuickPanelEntity = null,
  onNavigate,
  onOpenGroup,
  onCloseGroup,
  onExpandedChange,
  onOpenQuickPanel,
  onOpenFormDrawer,
}: {
  entry: ShellNavGroup;
  pathname: string;
  expanded: boolean;
  openGroups: Readonly<Record<string, boolean>>;
  activeQuickPanelEntity?: ShellNavTaxonomyEntity | null;
  onNavigate: (path: string) => void;
  onOpenGroup: (groupId: string) => void;
  onCloseGroup: (groupId: string) => void;
  onExpandedChange: (expanded: boolean) => void;
  onOpenQuickPanel?: (panelKey: string, params: Record<string, unknown>) => void;
  onOpenFormDrawer?: (drawerKey: string, params: Record<string, unknown>) => void;
}) {
  const Icon = entry.icon;
  const groupActive =
    isShellNavPathActive(pathname, entry) ||
    entry.children.some((child) =>
      isShellNavChildLinkActive(pathname, child, activeQuickPanelEntity ?? null),
    );
  const recordedOpen = openGroups[entry.id];
  const groupOpen = recordedOpen ?? groupActive;

  function handleParentClick() {
    // Clicar na categoria revela/oculta as subcategorias — nunca navega direto para a primeira.
    if (!expanded) {
      onExpandedChange(true);
      onOpenGroup(entry.id);
      return;
    }

    if (groupOpen) {
      onCloseGroup(entry.id);
      return;
    }

    onOpenGroup(entry.id);
  }

  function handleChildClick(child: ShellNavChildLink) {
    if (isShellNavChildRoute(child)) {
      if (child.enabled === false) {
        return;
      }
      onNavigate(child.path);
      return;
    }

    if (child.kind === 'formDrawer') {
      onOpenFormDrawer?.(child.drawerKey, {
        title: child.title ?? child.label,
      });
      return;
    }

    onOpenQuickPanel?.(child.panelKey, {
      entity: child.entity,
      title: child.title,
    });
  }

  return (
    <AppShellSidebarGroup open={groupOpen}>
      <AppShellSidebarItem
        active={groupActive}
        className="shell-sidebar__item--group"
        aria-label={entry.label}
        aria-expanded={expanded ? groupOpen : undefined}
        title={entry.label}
        icon={<Icon size={16} />}
        label={expanded ? entry.label : undefined}
        trailing={
          expanded ? (
            <AppShellSidebarGroupChevron>
              {groupOpen ? <ShellIconChevronLeft size={12} /> : <ShellIconChevronRight size={12} />}
            </AppShellSidebarGroupChevron>
          ) : undefined
        }
        onClick={handleParentClick}
      />

      {expanded && groupOpen ? (
        <AppShellSidebarChildren>
          {entry.children.map((child) => {
            const ChildIcon = child.icon;
            const childEnabled = !isShellNavChildRoute(child) || child.enabled !== false;
            const childActive =
              childEnabled &&
              isShellNavChildLinkActive(pathname, child, activeQuickPanelEntity ?? null);

            return (
              <AppShellSidebarChild
                key={child.id}
                active={childActive}
                aria-current={childActive ? 'page' : undefined}
                aria-label={child.label}
                disabled={!childEnabled}
                title={childEnabled ? child.label : `${child.label} (em breve)`}
                icon={<ChildIcon size={14} />}
                label={child.label}
                onClick={() => {
                  handleChildClick(child);
                }}
              />
            );
          })}
        </AppShellSidebarChildren>
      ) : null}
    </AppShellSidebarGroup>
  );
}

export function ExpandableNavSidebar({
  entries,
  pathname,
  expanded,
  openGroups,
  activeQuickPanelEntity = null,
  brandLabel = 'ZR System',
  footerExtras,
  onExpandedChange,
  onNavigate,
  onOpenGroup,
  onCloseGroup,
  onOpenQuickPanel,
  onOpenFormDrawer,
}: ExpandableNavSidebarProps) {
  const homeLink = resolveHomeLink(entries);

  function handleBrandActivate() {
    if (homeLink === undefined) {
      return;
    }

    onNavigate(homeLink.path);
  }

  function handleBrandKeyDown(event: KeyboardEvent<HTMLDivElement>) {
    if (event.key !== 'Enter' && event.key !== ' ') {
      return;
    }

    event.preventDefault();
    handleBrandActivate();
  }

  return (
    <AppShellSidebar expanded={expanded} aria-label="Navegação principal">
      <AppShellSidebarBrand
        {...(homeLink === undefined
          ? {}
          : {
              role: 'button' as const,
              tabIndex: 0,
              'aria-label': homeLink.label,
              onClick: handleBrandActivate,
              onKeyDown: handleBrandKeyDown,
            })}
      >
        <BrandMark size={20} />
        {expanded ? <AppShellSidebarBrandLabel>{brandLabel}</AppShellSidebarBrandLabel> : null}
      </AppShellSidebarBrand>

      <AppShellSidebarNav>
        {entries.map((entry) =>
          isShellNavGroup(entry) ? (
            <GroupNavItem
              key={entry.id}
              entry={entry}
              pathname={pathname}
              expanded={expanded}
              openGroups={openGroups}
              activeQuickPanelEntity={activeQuickPanelEntity}
              onNavigate={onNavigate}
              onOpenGroup={onOpenGroup}
              onCloseGroup={onCloseGroup}
              onExpandedChange={onExpandedChange}
              {...(onOpenQuickPanel ? { onOpenQuickPanel } : {})}
              {...(onOpenFormDrawer ? { onOpenFormDrawer } : {})}
            />
          ) : (
            <FlatNavItem
              key={entry.id}
              entry={entry}
              pathname={pathname}
              expanded={expanded}
              onNavigate={onNavigate}
            />
          ),
        )}
      </AppShellSidebarNav>

      <AppShellSidebarFooter>
        {footerExtras ? <div className="shell-sidebar__footer-extras">{footerExtras}</div> : null}
        <Button
          type="button"
          variant="ghost"
          size="sm"
          className="shell-sidebar__toggle"
          aria-label={expanded ? 'Recolher menu' : 'Expandir menu'}
          onClick={() => {
            onExpandedChange(!expanded);
          }}
        >
          {expanded ? (
            <>
              <ShellIconChevronLeft size={13} />
              Recolher
            </>
          ) : (
            <ShellIconChevronRight size={13} />
          )}
        </Button>
      </AppShellSidebarFooter>
    </AppShellSidebar>
  );
}
